import { useMeStore, type MeMetric } from "@/features/auth/hooks/useCurrentUser"
import { isPast, type QuotaMetric } from "@/lib/quotaGate"
import "./billing.css"

/** "2 left today", "Last one this month", or null when there is nothing to say (unlimited, plenty, or a once-only allowance). */
export function allowanceHintText(m: MeMetric | null | undefined): string | null {
  if (!m || m.limit === null || isPast(m.resets_at)) return null
  const when = m.window === "day" ? "today" : m.window === "month" ? "this month" : m.window === "draft" ? "in this draft" : null
  if (!when) return null // lifetime: a free first one is not "running low"
  const left = m.limit - m.used - (m.reserved ?? 0)
  if (left < 0 || left > 2) return null
  if (left === 0) return `None left ${when}`
  return left === 1 ? `Last one ${when}` : `${left} left ${when}`
}

/** A quiet line near a metered action, once two or fewer are left. Nothing for unlimited, plenty, exempt. */
export function AllowanceHint({ metric, className, data }: { metric: QuotaMetric; className?: string; data?: MeMetric | null }) {
  // `data` is a metric the caller fetched for one scope (a draft's edits); /me does not know it.
  const text = useMeStore((s) => {
    const e = s.me?.entitlements
    if (data) return e?.exempt ? null : allowanceHintText(data)
    if (!e || e.exempt) return null
    return allowanceHintText(e.metrics.find((m) => m.metric === metric))
  })
  if (!text) return null
  return <span className={`nab-hint${className ? ` ${className}` : ""}`} role="status">{text}</span>
}
